import { useCallback, useEffect, useState } from "react";
import { Box, Button, Container, Divider, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { formatCurrency, openFileInNewTab } from "@helpers/funciones";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetMotorbikeCatalogByIdQuery } from "@api/catalogs";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, TablaAmortizacionMicredimoto } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import useStatusConfig from "@hooks/useStatusConfig";
import {
  useLazyGetLoanApplicationByIdQuery,
  useLazyGetMotorbikeDetailByIdQuery,
  useLazyGetTablaAmortizacionMicredimotoQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { useNavigate } from "react-router-dom";
import CenteredLoader from "@components/CenteredLoader";
import ButtonDownloadTablaAmortizacion from "@components/ButtonDownloadTablaAmortizacion";
import { usePostDocumentGenarateMutation } from "@api/documents";

type FinancialRowItemProps = {
  label: string;
  value: number | string;
  valueSuffix?: string;
  isMobileOrTablet?: boolean;
};

type InformationProps = {
  imageUrl: string;
  name: string;
  price: number;
  months: number;
  weeks: number;
  license_plates_ownership: number;
  total_cost: number;
  down_payment: number;
  initial_payment: number;
  weekly_payments: number;
  daily_payments: number;
  amount_financed: number;
};

const SCREEN_STATUS = "CSAAO";

const FinancialRowItem = ({ label, value, valueSuffix, isMobileOrTablet }: FinancialRowItemProps) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Typography
        variant={isMobileOrTablet ? "body2" : "body1"}
        color="primary.main"
        fontWeight={500}
        textAlign="center"
      >
        {label}
      </Typography>
      <Typography
        variant={isMobileOrTablet ? "body2" : "body1"}
        color="primary.dark"
        fontWeight={500}
        textAlign="center"
      >
        {value}
        {valueSuffix && (
          <Typography
            component="span"
            variant={isMobileOrTablet ? "body3" : "body2"}
            color="primary.dark"
            fontWeight={500}
          >
            {" "}
            {valueSuffix}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

const CredimotoAprobado = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const navigate = useNavigate();

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);
  const legalRepresentative = useSelector((state: RootState) => state.register.legal_representative);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getMotorbikeDetailById] = useLazyGetMotorbikeDetailByIdQuery();
  const [getMotorbikeCatalogById] = useLazyGetMotorbikeCatalogByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [generateDocument] = usePostDocumentGenarateMutation();
  const [getTablaAmortizacion] = useLazyGetTablaAmortizacionMicredimotoQuery();

  // state
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [information, setInformation] = useState<InformationProps>({
    imageUrl: "",
    name: "",
    price: 0,
    months: 0,
    weeks: 0,
    license_plates_ownership: 0,
    total_cost: 0,
    down_payment: 0,
    initial_payment: 0,
    weekly_payments: 0,
    daily_payments: 0,
    amount_financed: 0,
  });

  const handleContinue = async () => {
    try {
      if (!application.id || !motorbikeInfo.motorbikeCatalogId) {
        throw new Error("Application or motorbike catalog ID is required");
      }

      if (!nextStatus) {
        throw new Error("Next status is required");
      }

      setIsSubmitting(true);

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      });

      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "CredimotoAprobado-handleContinue",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTable = async () => {
    try {
      setIsGeneratingDocument(true);
      const document_url = await generateDocument({
        loan_application_id: application.id,
        document_type: "tabla_amortizacion_mi_credimoto",
      }).unwrap();

      if (!document_url) {
        throw new Error("Error al generar el documento");
      }

      openFileInNewTab(document_url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "OfertaPF-handleDownloadTable",
        },
      });
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const getTableAmortization = useCallback(async (): Promise<TablaAmortizacionMicredimoto | null> => {
    try {
      const tableAmortization = await getTablaAmortizacion({
        solicitudId: application.id,
      }).unwrap();

      return tableAmortization;
    } catch {
      return null;
    }
  }, [getTablaAmortizacion, application.id]);

  const fetchInitialData = useCallback(async () => {
    try {
      if (!motorbikeInfo.motorbikeDetailId) {
        throw new Error("Motorbike catalog ID is required");
      }

      if (!application.id) {
        throw new Error("Application not found");
      }

      setIsLoading(true);

      const motorbikeDetailResponse = await getMotorbikeDetailById({
        solicitudId: application.id,
        motorbikeDetailId: motorbikeInfo.motorbikeDetailId,
      }).unwrap();

      if (!motorbikeDetailResponse.approved_details) {
        throw new Error("Approved details not found");
      }

      const [loanApplication, motorbikeCatalog, tablaAmortizacion] = await Promise.all([
        getLoanApplicationById(motorbikeDetailResponse.loan_application_id).unwrap(),
        getMotorbikeCatalogById(motorbikeDetailResponse.approved_details?.authorized_bike ?? "").unwrap(),
        getTableAmortization(),
      ]);

      if (!loanApplication || !motorbikeCatalog) {
        throw new Error("Loan application or motorbike catalog not found");
      }

      const {
        active_price,
        authorized_period,
        down_payment,
        amount_financed,
        total_cost,
        insurance_18m,
        license_plates_ownership,
      } = motorbikeDetailResponse.approved_details;

      const initial_payment = (active_price * down_payment) / 100;
      let weekly_payments = null;
      let daily_payments = null;

      if (tablaAmortizacion) {
        if (tablaAmortizacion.Amortizaciones.length > 0) {
          weekly_payments = tablaAmortizacion.Amortizaciones[0]?.PagoTotal ?? null;
          daily_payments = weekly_payments / 7;
        }
      }

      setInformation({
        imageUrl: motorbikeCatalog.image_path ?? "",
        name: `${motorbikeCatalog.name} ${motorbikeCatalog.data?.model}`,
        price: active_price,
        months: 0,
        weeks: authorized_period,
        license_plates_ownership: insurance_18m + license_plates_ownership,
        total_cost,
        down_payment: down_payment,
        initial_payment,
        weekly_payments: weekly_payments ?? 0,
        daily_payments: daily_payments ?? 0,
        amount_financed,
      });

      setIsDisabled(false);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "CredimotoAprobado-fetchInitialData",
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    motorbikeInfo.motorbikeDetailId,
    application.id,
    getMotorbikeDetailById,
    getLoanApplicationById,
    getMotorbikeCatalogById,
    getTableAmortization,
    dispatch,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        {isLoading ? (
          <CenteredLoader />
        ) : (
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="tofu_title" textAlign="center" color={"primary.dark"}>
              ¡Felicidades!
            </Typography>

            <Typography variant="tofu_title" textAlign="center" color={"primary.dark"}>
              {[
                legalRepresentative?.name || "",
                legalRepresentative?.last_name || "",
                legalRepresentative?.last_name_2 || "",
              ]
                .join(" ")
                .trim()}
            </Typography>

            <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, textAlign: "center" }}>
              <Typography
                sx={{ color: "primary.dark", mb: 1 }}
                variant={isMobileOrTablet ? "body2" : "body1"}
                textAlign="center"
                fontWeight={500}
              >
                El crédito para tu moto fue autorizado
              </Typography>

              <Typography
                variant={isMobileOrTablet ? "h6" : "h5"}
                fontWeight={700}
                sx={{ color: "primary.dark", mb: 2 }}
              >
                {information.name}
              </Typography>

              <Box
                component="img"
                src={information.imageUrl}
                alt={information.name}
                sx={{
                  width: "100%",
                  maxWidth: 280,
                  height: "auto",
                  maxHeight: 180,
                  objectFit: "contain",
                  mx: "auto",
                  display: "block",
                  mb: 2,
                }}
              />

              <Box sx={{ textAlign: "left", mb: 2 }}>
                <Box flex={1} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                  <FinancialRowItem
                    label="Precio de la motocicleta"
                    value={formatCurrency(information.price || 0)}
                    valueSuffix=" MXN"
                    isMobileOrTablet={isMobileOrTablet}
                  />
                  <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                  <FinancialRowItem
                    label="Plazo"
                    value={String(information.weeks || 0)}
                    valueSuffix=" semanas"
                    isMobileOrTablet={isMobileOrTablet}
                  />
                </Box>

                <Box flex={1} display="flex" flexDirection="row" justifyContent="center" alignItems="center" mt={4}>
                  <FinancialRowItem
                    label="Seguro, placas y tenencia"
                    value={formatCurrency(information.license_plates_ownership || 0)}
                    valueSuffix=" MXN"
                    isMobileOrTablet={isMobileOrTablet}
                  />
                  <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                  <FinancialRowItem
                    label="Costo Total"
                    value={formatCurrency(information.total_cost || 0)}
                    valueSuffix=" MXN"
                    isMobileOrTablet={isMobileOrTablet}
                  />
                </Box>

                <Box flex={1} display="flex" flexDirection="row" justifyContent="center" alignItems="center" mt={4}>
                  <FinancialRowItem
                    label={`Enganche (${information.down_payment}%)`}
                    value={formatCurrency(information.initial_payment || 0)}
                    valueSuffix=" MXN"
                    isMobileOrTablet={isMobileOrTablet}
                  />
                  <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                  <FinancialRowItem
                    label="Monto a Financiar"
                    value={formatCurrency(information.amount_financed || 0)}
                    valueSuffix=" MXN"
                    isMobileOrTablet={isMobileOrTablet}
                  />
                </Box>

                {information.weekly_payments > 0 && information.daily_payments > 0 && (
                  <Box flex={1} display="flex" flexDirection="row" justifyContent="center" alignItems="center" mt={4}>
                    <FinancialRowItem
                      label="Pago Semanal"
                      value={formatCurrency(information.weekly_payments || 0)}
                      valueSuffix=" MXN"
                      isMobileOrTablet={isMobileOrTablet}
                    />
                    <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                    <FinancialRowItem
                      label="Retención diaria"
                      value={formatCurrency(information.daily_payments || 0)}
                      valueSuffix=" MXN"
                      isMobileOrTablet={isMobileOrTablet}
                    />
                  </Box>
                )}
              </Box>

              <ButtonDownloadTablaAmortizacion
                sx={{ mt: isMobileOrTablet ? 4 : 6 }}
                disabled={isGeneratingDocument}
                onClick={handleDownloadTable}
              />
              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  type="submit"
                  variant="default"
                  disabled={isLoading || isDisabled || isSubmitting}
                  onClick={handleContinue}
                >
                  Continuar
                </Button>
              </Box>
            </Container>
          </Box>
        )}
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CredimotoAprobado;
